import { useCallback, useEffect, useMemo, useState } from "react";
import type { GridPaginationModel } from "@mui/x-data-grid";
import {
  aplicarAjusteEstoque,
  listarItensEstoque,
  listarMovimentacoesEstoque,
  obterResumoEstoque,
} from "services/lojaEstoque";
import type {
  IEstoqueItemRow,
  IEstoqueMovRow,
  IEstoqueResumo,
  TipoMovimentacaoEstoque,
} from "interfaces/loja/estoque";
import { setMessage, useMaterialUIController } from "context";

const PAGE_ITENS = 25;
const PAGE_MOVS = 25;

export function useEstoqueLoja() {
  const [, dispatchCtx] = useMaterialUIController();

  const [resumo, setResumo] = useState<IEstoqueResumo | null>(null);
  const [resumoLoading, setResumoLoading] = useState(true);

  const [itensRows, setItensRows] = useState<IEstoqueItemRow[]>([]);
  const [itensTotal, setItensTotal] = useState(0);
  const [itensPage, setItensPage] = useState(1);
  const [itensLoading, setItensLoading] = useState(false);
  const [buscaItensDraft, setBuscaItensDraft] = useState("");
  const [qItens, setQItens] = useState("");
  const [somenteBaixo, setSomenteBaixo] = useState(false);
  const [limiteBaixo, setLimiteBaixo] = useState(5);

  const [movRows, setMovRows] = useState<IEstoqueMovRow[]>([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movPage, setMovPage] = useState(1);
  const [movLoading, setMovLoading] = useState(false);
  const [movTipo, setMovTipo] = useState<TipoMovimentacaoEstoque | "">("");
  const [movSku, setMovSku] = useState("");
  const [movDataInicio, setMovDataInicio] = useState("");
  const [movDataFim, setMovDataFim] = useState("");

  const loadResumo = useCallback(async () => {
    setResumoLoading(true);
    try {
      const { data, status } = await obterResumoEstoque({ limiteBaixo });
      if (status === 200) setResumo(data);
    } catch {
      setResumo(null);
    } finally {
      setResumoLoading(false);
    }
  }, [limiteBaixo]);

  const loadItens = useCallback(async () => {
    setItensLoading(true);
    try {
      const { data, status } = await listarItensEstoque({
        page: itensPage,
        limit: PAGE_ITENS,
        query: qItens || undefined,
        somenteBaixo: somenteBaixo || undefined,
        limiteBaixo,
      });
      if (status === 200) {
        setItensRows(data.data);
        setItensTotal(data.meta.totalRecord);
      }
    } finally {
      setItensLoading(false);
    }
  }, [itensPage, qItens, somenteBaixo, limiteBaixo]);

  const loadMovs = useCallback(async () => {
    setMovLoading(true);
    try {
      const { data, status } = await listarMovimentacoesEstoque({
        page: movPage,
        limit: PAGE_MOVS,
        tipo: movTipo || undefined,
        sku: movSku || undefined,
        dataInicio: movDataInicio || undefined,
        dataFim: movDataFim || undefined,
      });
      if (status === 200) {
        setMovRows(data.data);
        setMovTotal(data.meta.totalRecord);
      }
    } finally {
      setMovLoading(false);
    }
  }, [movPage, movTipo, movSku, movDataInicio, movDataFim]);

  useEffect(() => {
    const id = window.setTimeout(() => setQItens(buscaItensDraft.trim()), 450);
    return () => window.clearTimeout(id);
  }, [buscaItensDraft]);

  useEffect(() => {
    void loadResumo();
  }, [loadResumo]);

  useEffect(() => {
    setItensPage(1);
  }, [qItens, somenteBaixo, limiteBaixo]);

  useEffect(() => {
    void loadItens();
  }, [loadItens]);

  useEffect(() => {
    void loadMovs();
  }, [loadMovs]);

  const onItensPagination = useCallback((model: GridPaginationModel) => {
    setItensPage(model.page + 1);
  }, []);

  const onMovPagination = useCallback((model: GridPaginationModel) => {
    setMovPage(model.page + 1);
  }, []);

  const aplicarAjuste = useCallback(
    async (payload: {
      variacaoId?: string;
      produtoId?: string;
      delta: number;
      observacao: string;
    }) => {
      const res = await aplicarAjusteEstoque(payload);
      const { data, status } = res;
      if (status === 201 && data && "novoEstoque" in data) {
        setMessage(dispatchCtx, {
          open: true,
          severity: "success",
          text: `Ajuste registrado. Novo saldo: ${data.novoEstoque}`,
        });
        await Promise.all([loadResumo(), loadItens(), loadMovs()]);
        return true;
      }
      const errBody = data as unknown as { error?: string };
      const errMsg =
        typeof errBody?.error === "string"
          ? errBody.error
          : "Não foi possível aplicar o ajuste.";
      setMessage(dispatchCtx, {
        open: true,
        severity: "error",
        text: errMsg,
      });
      return false;
    },
    [dispatchCtx, loadResumo, loadItens, loadMovs]
  );

  const itensTotalPages = useMemo(
    () => Math.max(1, Math.ceil(itensTotal / PAGE_ITENS)),
    [itensTotal]
  );
  const movTotalPages = useMemo(
    () => Math.max(1, Math.ceil(movTotal / PAGE_MOVS)),
    [movTotal]
  );

  return {
    resumo,
    resumoLoading,
    refetchResumo: loadResumo,
    itensRows,
    itensTotal,
    itensPage,
    itensLoading,
    itensPageSize: PAGE_ITENS,
    itensTotalPages,
    buscaItensDraft,
    setBuscaItensDraft,
    qItens,
    somenteBaixo,
    setSomenteBaixo,
    limiteBaixo,
    setLimiteBaixo,
    setItensPage,
    onItensPagination,
    loadItens,
    movRows,
    movTotal,
    movPage,
    movLoading,
    movPageSize: PAGE_MOVS,
    movTotalPages,
    movTipo,
    setMovTipo,
    movSku,
    setMovSku,
    movDataInicio,
    setMovDataInicio,
    movDataFim,
    setMovDataFim,
    setMovPage,
    onMovPagination,
    loadMovs,
    aplicarAjuste,
  };
}
